import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { ArrowLeft, ArrowRight, Loader2, Mail, Lock } from "lucide-react";

type AuthSearch = { redirect?: string };

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Entrar — Plataforma 101" },
      { name: "description", content: "Acesse sua Plataforma 101." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): AuthSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: AuthPage,
});

type Mode = "login" | "forgot" | "magic";

function sanitizeRedirect(raw: string | undefined): string {
  if (!raw) return "/membros";
  try {
    // Only same-origin relative paths
    if (raw.startsWith("/") && !raw.startsWith("//")) return raw;
    const url = new URL(raw, window.location.origin);
    if (url.origin === window.location.origin) return url.pathname + url.search + url.hash;
  } catch {
    /* noop */
  }
  return "/membros";
}

function AuthPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/auth" });
  const destination = typeof window !== "undefined" ? sanitizeRedirect(search.redirect) : "/membros";

  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState<{ type: "err" | "ok"; text: string } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        let target = destination;
        try {
          const saved = sessionStorage.getItem("post_login_redirect");
          if (saved) {
            target = sanitizeRedirect(saved);
            sessionStorage.removeItem("post_login_redirect");
          }
        } catch { /* noop */ }
        navigate({ to: target as any, replace: true });
      }
    });
  }, [navigate, destination]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    setLoading(true);
    try {
      if (mode === "login") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate({ to: destination as any, replace: true });
      } else if (mode === "forgot") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setMsg({ type: "ok", text: "Enviamos um e-mail com o link de recuperação." });
      } else {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: { emailRedirectTo: `${window.location.origin}${destination}` },
        });
        if (error) throw error;
        setMsg({ type: "ok", text: "Enviamos um link mágico para o seu e-mail." });
      }
    } catch (err) {
      setMsg({
        type: "err",
        text: err instanceof Error ? err.message : "Não foi possível concluir.",
      });
    } finally {
      setLoading(false);
    }
  }

  const title =
    mode === "login"
      ? "Bem-vindo de volta."
      : mode === "forgot"
        ? "Recuperar senha"
        : "Entrar por link mágico";
  const subtitle =
    mode === "login"
      ? "Entre para continuar explorando os 101 negócios."
      : mode === "forgot"
        ? "Enviaremos um link para você criar uma nova senha."
        : "Enviaremos um link direto ao seu e-mail.";
  const submitLabel =
    mode === "login" ? "Entrar" : mode === "forgot" ? "Enviar link de recuperação" : "Enviar link mágico";

  return (
    <div className="min-h-screen bg-[var(--beige)] font-body text-[var(--navy)]">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-10">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 font-display text-xl font-extrabold">
          <ArrowLeft size={16} className="text-[var(--gray-ink)]" />
          Projeto <span className="text-[var(--green)]">101</span>
        </Link>

        <div className="animate-fade-in rounded-2xl border border-black/5 bg-white p-8 shadow-[0_1px_2px_rgba(12,27,51,0.04),0_20px_40px_-24px_rgba(12,27,51,0.15)]">
          <h1 className="font-display text-3xl font-extrabold leading-tight">{title}</h1>
          <p className="mt-2 text-sm text-[var(--gray-ink)]">{subtitle}</p>

          {mode === "login" && (
            <>
              <button
                type="button"
                disabled={loading}
                onClick={async () => {
                  setMsg(null);
                  setLoading(true);
                  try {
                    sessionStorage.setItem("post_login_redirect", destination);
                  } catch { /* noop */ }
                  const result = await lovable.auth.signInWithOAuth("google", {
                    redirect_uri: window.location.origin + "/auth",
                  });
                  if (result.error) {
                    setLoading(false);
                    setMsg({ type: "err", text: result.error.message || "Não foi possível entrar com o Google." });
                    return;
                  }
                  if (result.redirected) return;
                  navigate({ to: destination as any, replace: true });
                }}
                className="mt-6 flex w-full items-center justify-center gap-3 rounded-lg border border-black/10 bg-white px-4 py-3 text-sm font-semibold text-[var(--navy)] transition hover:-translate-y-0.5 hover:border-black/20 hover:shadow-sm disabled:pointer-events-none disabled:opacity-60"
                aria-label="Entrar com Google"
              >
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.2 0 9.8-2 13.3-5.2l-6.1-5.2C29.2 34.8 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.6l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.1 5.2C41 34.1 44 29.5 44 24c0-1.3-.1-2.4-.4-3.5z"/>
                </svg>
                Entrar com Google
              </button>

              <div className="mt-5 flex items-center gap-3 text-xs uppercase tracking-wide text-[var(--gray-ink)]">
                <span className="h-px flex-1 bg-black/10" />
                ou com e-mail
                <span className="h-px flex-1 bg-black/10" />
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
                E-mail
              </label>
              <div className="relative mt-1">
                <Mail
                  size={15}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gray-ink)]"
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-black/10 bg-white py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-[var(--green)] focus:ring-2 focus:ring-[var(--green)]/15"
                  placeholder="voce@email.com"
                  autoComplete="email"
                />
              </div>
            </div>
            {mode === "login" && (
              <div>
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
                    Senha
                  </label>
                  <button
                    type="button"
                    onClick={() => setMode("forgot")}
                    className="text-xs font-semibold text-[var(--green)] hover:underline"
                  >
                    Esqueci minha senha
                  </button>
                </div>
                <div className="relative mt-1">
                  <Lock
                    size={15}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gray-ink)]"
                  />
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg border border-black/10 bg-white py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-[var(--green)] focus:ring-2 focus:ring-[var(--green)]/15"
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                </div>
              </div>
            )}

            {msg && (
              <div
                className={`animate-fade-in rounded-lg px-3 py-2 text-sm ${
                  msg.type === "err"
                    ? "bg-red-50 text-red-700"
                    : "bg-emerald-50 text-emerald-700"
                }`}
              >
                {msg.text}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="group flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--navy)] px-4 py-3 text-sm font-semibold text-white shadow-md shadow-[var(--navy)]/15 transition hover:-translate-y-0.5 hover:bg-[var(--navy)]/90 disabled:pointer-events-none disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={15} className="animate-spin" /> Entrando...
                </>
              ) : (
                <>
                  {submitLabel}
                  <ArrowRight size={14} className="transition group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 space-y-2 text-center text-sm">
            {mode !== "login" && (
              <button
                type="button"
                className="text-[var(--gray-ink)] hover:text-[var(--navy)]"
                onClick={() => setMode("login")}
              >
                Voltar para entrar com senha
              </button>
            )}
            {mode === "login" && (
              <button
                type="button"
                className="text-[var(--gray-ink)] hover:text-[var(--navy)]"
                onClick={() => setMode("magic")}
              >
                Entrar por link mágico
              </button>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-[var(--gray-ink)]">
          Não possui acesso?{" "}
          <Link to="/" className="font-semibold text-[var(--navy)] hover:text-[var(--green)] hover:underline">
            Conheça a Plataforma
          </Link>
        </p>
      </div>
    </div>
  );
}
